import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Avatar, Card, CardContent, CardActions, useTheme, useMediaQuery, Snackbar, Alert } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BuildIcon from '@mui/icons-material/Build';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { Link } from 'react-router-dom';
import ReportRepairDialog from '../../Components/Dialogs/ReportRepairDialog';

interface CurrentServiceCommentsProps {
  comments: any[];
  users: any[];
  onDataRefresh?: () => Promise<void>;
}

const CurrentServiceComments: React.FC<CurrentServiceCommentsProps> = ({ comments, users, onDataRefresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [selectedBoxNumber, setSelectedBoxNumber] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleRepairClick = (boxNumber: number) => {
    setSelectedBoxNumber(boxNumber);
    setRepairDialogOpen(true);
  };

  const handleRepairDialogClose = () => {
    setRepairDialogOpen(false);
    setSelectedBoxNumber(null);
  };

  const handleRepairSuccess = async (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    
    // Refresh data in parent component
    if (onDataRefresh) {
      await onDataRefresh();
    }
  };

  const handleRepairError = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ width: isMobile ? '100%' : '48%', display: 'flex', flexDirection: 'column', mb: isMobile ? 4 : 0 }}>
      <Typography variant="h4" gutterBottom>
        Current Service Comments ({comments.length})
      </Typography>
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        maxHeight: isMobile ? '60vh' : '90vh',
      }}>
        {comments.map((comment) => {
          const user = users.find(u => u.username === comment.username);
          return (
            <Card key={comment.box} sx={{ mb: 2, p: 2, backgroundColor: theme.palette.background.paper, borderRadius: '8px', border: `1px solid ${theme.palette.borders.primary}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', wordBreak: 'break-word' }}>
                <Avatar src={user?.profile_image_path} alt={comment.username} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1, mr: 2 }}>
                  <Typography variant="body2">
                    <strong>Username:</strong> {comment.username}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Comment:</strong> {comment.comment}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.25, borderRadius: '8px', bgcolor: alpha(theme.palette.text.primary, 0.1) }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontFamily: 'monospace' }}>
                        Box {comment.box}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'inline-flex', px: 0.75, py: 0.25, borderRadius: '8px', bgcolor: alpha(theme.palette.text.primary, 0.14) }}>
                      <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontFamily: 'monospace' }}>
                        {new Date(comment.time_stamp).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <CardActions sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="View Device Panel">
                    <IconButton component={Link} to={`/device/${comment.box}`} color="primary">
                      <AssessmentIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Report Repair">
                    <IconButton onClick={() => handleRepairClick(comment.box)} color="primary">
                      <BuildIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </CardContent>
            </Card>
          );
        })}
      </Box>
      
      <ReportRepairDialog
        open={repairDialogOpen}
        onClose={handleRepairDialogClose}
        deviceNumber={selectedBoxNumber || ''}
        onSuccess={handleRepairSuccess}
        onError={handleRepairError}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CurrentServiceComments;
